import { SealPercent } from '@phosphor-icons/react'
import React from 'react'
import { DiscountMethod, UiConfigDiscountType, DiscountFieldPaths } from 'src/lib/types/discountsV2'
import { useDiscountForm } from '../../hooks/useDiscountForm'
import { Icon } from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'
import { discountCreationModes } from 'src/lib/utils/discounts/constants/discountConstants'
import {
  CODE_ALLOWED_REGEX,
  CODE_MAX_LENGTH,
  sanitizeForCode,
  TITLE_MAX_LENGTH
} from 'src/lib/utils/discounts/discountsV2Helpers'
import FormErrorText from 'src/components/common/FormErrorText'
import { SectionWrapper } from 'src/components/common/context/SectionWrapper'
import { useLocale } from 'src/lib/hooks/useLocal'
import { LightbulbFilament } from '@phosphor-icons/react'

const DiscountMethodV2 = () => {
  const {
    state: { entitlement, uiConfig, errors, prerequisite },
    actions: { setFormState, registerRef }
  } = useDiscountForm()
  const { t } = useLocale()

  const { applicationMethod } = entitlement

  const isExitCoupon = uiConfig.type === UiConfigDiscountType.EXIT_COUPON
  const isAutomatic = isExitCoupon || applicationMethod.method === DiscountMethod.AUTOMATIC

  const handleMethodChange = (method: DiscountMethod) => {
    if (method === applicationMethod.method) return
    const isSwitchingToCode = method === DiscountMethod.CODE
    const isSwitchingToAutomatic = method === DiscountMethod.AUTOMATIC
    const newDiscountCode = isSwitchingToCode ? sanitizeForCode(applicationMethod.title) : applicationMethod.discountCode
    const newTitle = isSwitchingToAutomatic 
      ? applicationMethod.discountCode
      : ''

    setFormState({
      entitlement: {
        ...entitlement,
        applicationMethod: {
          ...applicationMethod,
          discountCode: newDiscountCode,
          title: newTitle,
          method,
          autoApplicable: method === DiscountMethod.AUTOMATIC
        }
      },
      prerequisite: {
        ...prerequisite,
        availability: {
          ...prerequisite.availability,
          discoverable: method === DiscountMethod.AUTOMATIC ? false : prerequisite.availability.discoverable
        }
      }
    })
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (isAutomatic) {
      if (value.length > TITLE_MAX_LENGTH) return
      setFormState(
        {
          entitlement: {
            ...entitlement,
            applicationMethod: {
              ...applicationMethod,
              title: value
            }
          }
        },
        [DiscountFieldPaths.METHOD_TITLE]
      )
    } else {
      const formattedValue = value.toUpperCase().replace(CODE_ALLOWED_REGEX, '')
      if (formattedValue.length > CODE_MAX_LENGTH) return
      setFormState(
        {
          entitlement: {
            ...entitlement,
            applicationMethod: {
              ...applicationMethod,
              discountCode: formattedValue,
              title: formattedValue
            }
          }
        },
        [DiscountFieldPaths.METHOD_CODE]
      )
    }
  }

  const getLabel = () => {
    return discountCreationModes[uiConfig.type]?.label
  }

  const displayValue = isAutomatic ? applicationMethod.title : applicationMethod.discountCode
  return (
    <div className='flex flex-col gap-5'>
      <SectionWrapper sectionKey='discount-method-input' className='rounded-xl' overlayClassName='-inset-4'>
        <div
          className='flex flex-col gap-2'
          ref={node =>
            registerRef(isAutomatic ? DiscountFieldPaths.METHOD_TITLE : DiscountFieldPaths.METHOD_CODE, node)
          }
        >
          <div className='flex items-center gap-2'>
            <Icon IconComponent={SealPercent} size='xs' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
            <span className='text-text-xs-medium text-zinc-500'>{getLabel()}</span>
          </div>
          <input
            id='discount-method-input'
            type='text'
            placeholder={
              isAutomatic ? t('discounts_v2.method_title_placeholder') : t('discounts_v2.method_code_placeholder')
            }
            value={displayValue}
            onChange={handleValueChange}
            className='text-display-xs-semibold text-zinc-800 border-none outline-none placeholder:text-zinc-400/50 w-full bg-transparent'
          />
          <FormErrorText
            message={isAutomatic ? errors[DiscountFieldPaths.METHOD_TITLE] : errors[DiscountFieldPaths.METHOD_CODE]}
          />
        </div>
      </SectionWrapper>

      {isExitCoupon ? (
        <div className='flex items-start gap-2 py-2.5'>
          <div className='flex items-center gap-2 p-2 rounded-[10px] border border-black/[0.04] bg-sky-50 shrink-0'>
            <Icon IconComponent={LightbulbFilament} size='xs' className='text-sky-600' />
          </div>
          <div className='flex flex-col gap-0.5'>
            <p className='text-text-xs-medium text-sky-600'>
              {t('discounts_v2.exit_coupon_method_info')}
            </p>
            <p className='text-text-xs-medium text-zinc-500'>
              {t('discounts_v2.exit_coupon_method_nav')}
            </p>
          </div>
        </div>
      ) : (
        <SectionWrapper sectionKey='discount-method-type' className='rounded-xl' overlayClassName='-inset-4'>
          <div className='flex gap-3       '>
            <div
              id='discount-method-code'
              onClick={() => handleMethodChange(DiscountMethod.CODE)}
              className={classNames(
                'flex-1 px-4 py-3 rounded-2xl border-[1.5px] transition-all cursor-pointer bg-white',
                applicationMethod.method === DiscountMethod.CODE
                  ? 'border-zinc-500 shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'
                  : 'border-transparent shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'
              )}
            >
              <p className='text-text-sm-medium text-zinc-700'>{t('discounts_v2.use_as_code')}</p>
              <p className='text-text-xs-regular text-zinc-500 '>{t('discounts_v2.use_as_code_desc')}</p>
            </div>
            <div
              id='discount-method-automatic'
              onClick={() => handleMethodChange(DiscountMethod.AUTOMATIC)}
              className={classNames(
                'flex-1 px-4 py-3 rounded-2xl border-[1.5px] transition-all cursor-pointer bg-white',
                applicationMethod.method === DiscountMethod.AUTOMATIC
                  ? 'border-zinc-500 shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'
                  : 'border-transparent shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'
              )}
            >
              <p className='text-text-sm-medium text-zinc-700'>{t('discounts_v2.automatic')}</p>
              <p className='text-text-xs-regular text-zinc-500'>{t('discounts_v2.automatic_desc')}</p>
            </div>
          </div>
        </SectionWrapper>
      )}
    </div>
  )
}

export default DiscountMethodV2
